import { useEffect, useMemo, useState } from "react";
import { api, formatBRL } from "../lib/api";
import { Crown, Medal, Trophy, Smartphone, Headphones, Signal, Shield, DollarSign, Zap, BarChart3 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Avatar } from "../components/Avatar";

const INDICATORS = [
  { k: "xp", label: "XP", icon: Zap, color: "text-fuchsia-300", chip: "chip-pink" },
  { k: "total_value", label: "Faturamento", icon: DollarSign, color: "text-amber-300", chip: "chip-gold", isMoney: true },
  { k: "aparelho", label: "Aparelhos", icon: Smartphone, color: "text-violet-300", chip: "chip" },
  { k: "acessorio", label: "Acessórios", icon: Headphones, color: "text-cyan-300", chip: "chip-cyan" },
  { k: "plano", label: "Planos", icon: Signal, color: "text-emerald-300", chip: "chip-green" },
  { k: "seguro", label: "Seguros", icon: Shield, color: "text-amber-300", chip: "chip-gold" },
];

export default function Ranking() {
  const [data, setData] = useState([]);
  const [tab, setTab] = useState("geral"); // geral | detalhado
  const { user } = useAuth();

  useEffect(() => {
    api.get("/ranking/general").then((r) => setData(r.data.general || [])).catch(() => {});
  }, []);

  const me = useMemo(() => data.find((d) => d.id === user?.id), [data, user]);
  const top3 = data.slice(0, 3);
  const podiumOrder = [top3[1], top3[0], top3[2]].filter(Boolean);

  return (
    <div className="arcade-grid min-h-screen pb-28">
      <div className="phone-frame phone-frame-full px-4 pt-6">
        <div className="mb-4">
          <p className="chip chip-pink mb-2">🏆 Hall da fama</p>
          <h1 className="font-arcade text-3xl font-extrabold text-white tracking-tight">
            Placar dos Campeões
          </h1>
          <p className="text-sm text-slate-400 mt-1">Combina XP, faturamento e todas as categorias</p>
        </div>

        {/* Tabs */}
        <div className="grid grid-cols-2 gap-1.5 bg-slate-900/60 p-1 rounded-xl border border-slate-800 mb-5">
          <button
            data-testid="ranking-tab-geral"
            onClick={() => setTab("geral")}
            className={`py-2.5 rounded-lg text-sm font-arcade font-semibold flex items-center justify-center gap-1.5 transition-colors ${
              tab === "geral" ? "bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white" : "text-slate-400"
            }`}
          >
            <Trophy size={14} /> Ranking geral
          </button>
          <button
            data-testid="ranking-tab-detalhado"
            onClick={() => setTab("detalhado")}
            className={`py-2.5 rounded-lg text-sm font-arcade font-semibold flex items-center justify-center gap-1.5 transition-colors ${
              tab === "detalhado" ? "bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white" : "text-slate-400"
            }`}
          >
            <BarChart3 size={14} /> Meu desempenho
          </button>
        </div>

        {tab === "geral" && (
          <>
            {/* Podium */}
            <div className="neon-card-gold p-5 mb-5" data-testid="podium-container">
              <div className="grid grid-cols-3 items-end gap-2 min-h-[200px]">
                {podiumOrder.map((p, idx) => {
                  if (!p) return <div key={idx} />;
                  const place = idx === 0 ? 2 : idx === 1 ? 1 : 3;
                  const styles = {
                    1: { h: "h-32", glow: "avatar-gold", icon: Crown, color: "from-amber-400 to-yellow-500", testid: "podium-first" },
                    2: { h: "h-24", glow: "avatar-silver", icon: Medal, color: "from-slate-300 to-slate-400", testid: "podium-second" },
                    3: { h: "h-20", glow: "avatar-bronze", icon: Medal, color: "from-orange-400 to-amber-600", testid: "podium-third" },
                  }[place];
                  const Ico = styles.icon;
                  return (
                    <div key={p.id} className="flex flex-col items-center" data-testid={styles.testid}>
                      <div className={`mb-2 ${styles.glow} rounded-full`}>
                        <Avatar user={p} size={64} />
                      </div>
                      <p className="font-arcade text-xs font-bold text-white text-center line-clamp-1 w-full">
                        {p.name}
                      </p>
                      <p className="font-mono text-[10px] text-slate-300 mb-1">{p.xp} XP</p>
                      <div className={`${styles.h} w-full rounded-t-xl bg-gradient-to-t ${styles.color} flex items-start justify-center pt-2 shadow-lg`}>
                        <Ico size={18} className="text-white" strokeWidth={2.8} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* List */}
            <div className="space-y-2" data-testid="ranking-list">
              {data.slice(3).map((p) => {
                const isMe = p.id === user?.id;
                return (
                  <div
                    key={p.id}
                    className={`neon-card p-4 ${isMe ? "ring-2 ring-fuchsia-500/50" : ""}`}
                    data-testid={`ranking-row-${p.rank}`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="font-arcade text-lg font-bold text-slate-400 w-8">#{p.rank}</span>
                      <Avatar user={p} size={40} />
                      <div className="flex-1 min-w-0">
                        <p className="font-arcade text-sm font-bold text-white truncate">
                          {p.name} {isMe && <span className="chip chip-pink ml-1">você</span>}
                        </p>
                        <p className="text-[11px] font-mono text-slate-400">
                          {p.xp} XP • {formatBRL(p.total_value)}
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-4 gap-1.5 mt-3">
                      {INDICATORS.slice(2).map((ind) => (
                        <div key={ind.k} className="bg-slate-900/50 rounded-lg py-1.5 text-center">
                          <p className="font-arcade text-sm font-bold text-white">{p[ind.k]}</p>
                          <p className="text-[9px] font-mono text-slate-400 uppercase tracking-wider">{ind.label}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
              {data.length === 0 && (
                <p className="text-center text-slate-500 py-8 text-sm">Nenhum vendedor ainda</p>
              )}
            </div>
          </>
        )}

        {tab === "detalhado" && (
          <div data-testid="ranking-detailed">
            {!me && (
              <p className="text-center text-slate-500 py-10 text-sm">
                Você aparece aqui depois da primeira venda ou bônus.
              </p>
            )}
            {me && (
              <>
                <div className="neon-card-violet p-5 mb-5">
                  <div className="flex items-center gap-3 mb-4">
                    <Avatar user={me} size={56} ringClass="avatar-glow" />
                    <div>
                      <p className="font-arcade text-xl font-bold text-white">{me.name}</p>
                      <p className="text-xs font-mono text-fuchsia-300">
                        Posição geral: <span className="font-bold text-white">#{me.rank}</span>
                      </p>
                    </div>
                  </div>
                </div>

                <h2 className="font-arcade text-sm font-bold text-slate-300 uppercase tracking-widest mb-3">
                  Seu desempenho por indicador
                </h2>
                <div className="space-y-3" data-testid="my-breakdown">
                  {INDICATORS.map((ind) => {
                    const Ico = ind.icon;
                    const value = me[ind.k];
                    const rank = me.ranks?.[ind.k];
                    const isLeader = rank === 1;
                    return (
                      <div key={ind.k} className="neon-card p-4 flex items-center gap-3" data-testid={`indicator-${ind.k}`}>
                        <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${isLeader ? "bg-gradient-to-br from-amber-400 to-pink-500 shadow-lg shadow-amber-500/30" : "bg-slate-800"}`}>
                          <Ico size={20} className="text-white" strokeWidth={2.4} />
                        </div>
                        <div className="flex-1">
                          <p className="font-arcade text-xs text-slate-400 uppercase tracking-widest">{ind.label}</p>
                          <p className="font-arcade text-xl font-bold text-white">
                            {ind.isMoney ? formatBRL(value) : value}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className={`font-arcade text-lg font-bold ${isLeader ? "text-amber-300" : "text-slate-300"}`}>
                            #{rank || "-"}
                          </p>
                          {isLeader && <p className="text-[10px] font-mono text-amber-300 uppercase">👑 líder</p>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
